(ns frontend.view.settings2.kyc.view
  (:require
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.view.not-found :as not-found]
   [frontend.view.settings2.sales-pipelines.db :as db]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [frontend.view.settings2.kyc.sub :as sub]
   [frontend.view.settings2.kyc.event :as event]
   [re-frame.core :as rf]
   [ui.editors.data-input :as data-input]
   [ui.components.modal.view :as modal]
   [ui.util :as uu]
   [ui.dom-globals :as dg]
   [common.localization :as loc]
   [clojure.string :as string]))

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [_]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/kyc)]])

(defn very-simple-select
  [{:keys [value options on-change disabled?]}]
  (let [kw-value? (keyword? value)
        value (if kw-value?
                (name value)
                (str value))]
    [:select
     {:disabled (boolean disabled?)
      :value value
      :class (uu/cm "px-2 py-1.5 border m-0 border-input-light-border-paler rounded text-sm"
                    "border-secondary-light-border"
                    "focus-visible:outline-primary-regular")
      :on-change (fn [e]
                   (let [new-value-str (dg/event-target-value e)
                         value (when (and new-value-str
                                          (not-empty new-value-str))
                                 (if kw-value?
                                   (keyword new-value-str)
                                   new-value-str))]
                     (on-change value)))}
     (->> options
          (map (fn [{:keys [value label]}]
                 ^{:key (str value)}
                 [:option
                  {:value (if (keyword? value)
                            (name value)
                            value)}
                  label]))
          doall)]))

(defn very-simple-number-input
  [{:keys [value on-change disabled?]}]
  [:input
   {:type "number"
    :min 1
    :max 52
    :disabled (boolean disabled?)
    :value value
    :class (uu/cm "w-16 px-2 py-1 m-0 border border-input-light-border-paler rounded text-sm"
                  "border-secondary-light-border"
                  "focus-visible:outline-primary-regular")
    :on-change (fn [e]
                 (let [new-value (-> e
                                     dg/event-target-value
                                     js/parseInt)
                       new-value (if (js/isNaN new-value)
                                   nil
                                   new-value)]
                   (on-change new-value)))}])

(defn divider-h
  []
  [:div
   {:class "w-full h-px"
    :style {:background-color "rgba(0,0,0,0.1)"}}])

(defn reminder-configuration-item
  [{:keys [reminder-index reminder]}]
  (let [timing-after-label (case reminder-index
                             0 (loc/tr :initial-kyc-invite-lc)
                             1 (loc/tr :first-reminder-lc))
        timing-helper-label (loc/tr :kyc-reminder-timing-helper-label
                                    [timing-after-label])]
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "flex gap-2 items-center"}
      [:p
       {:class "font-semibold text-lg"}
       (loc/tr (case reminder-index
                 0 :first-reminder-email
                 1 :second-reminder-email))]]

     [:div
      {:class "grid grid-cols-2 gap-2"}
      [:div
       {:class "flex flex-col gap-3"}
       [:div
        {:class "flex items-center gap-2"}
        [:i {:class "fal fa-clock"}]
        [:p
         {:class "font-semibold"}
         (loc/tr :timing)]]
       [:p
        {:class "text-base-light-text-paler text-pretty"}
        timing-helper-label]
       [:div
        {:class "flex items-center flex-nowrap gap-2"}
        [:div
         [very-simple-number-input
          {:value (get-in reminder [:after :value])
           :on-change (fn [new-value]
                        (rf/dispatch [::event/set-reminder-interval-value reminder-index new-value]))}]]

        [very-simple-select
         {:value (get-in reminder [:after :unit])
          :options [{:value :weeks
                     :label (loc/tr :weeks)}
                    {:value :days
                     :label (loc/tr :days)}
                    {:value :minutes
                     :label (loc/tr :minutes)}]
          :on-change (fn [new-unit]
                       (rf/dispatch [::event/set-reminder-interval-unit reminder-index new-unit]))}]

        [:p
         {:class "text-nowrap"}
         (str (loc/tr :after-lc)
              " "
              timing-after-label)]]]

      [:div
       {:class "flex flex-col gap-2"}
       [:div
        {:class "flex gap-2 items-center"}
        [:i {:class "fal fa-envelope"}]
        [:p
         {:class "font-semibold"}
         (loc/tr :email-content)]]

       [button/sm-secondary-hollow
        {:on-click #(rf/dispatch [::event/open-reminder-email-preview reminder-index])}
        [:i {:class "fal fa-eye"}]
        (loc/tr :view-full-email)]]]]))

(defn reminders-list
  []
  (let [reminders @(rf/subscribe [::sub/reminders])]
    [:div
     {:class "flex flex-col gap-5"}
     (->> reminders
          (map-indexed
           (fn [idx reminder]
             (let [last? (= idx (dec (count reminders)))]
               ^{:key (str idx)}
               [:<>
                ^{:key (str "reminder-" idx)}
                [reminder-configuration-item
                 {:reminder reminder
                  :reminder-index idx}]
                (when-not last?
                  ^{:key (str "reminder-divider-" idx)}
                  [divider-h])]))))]))

(defn reminders-enabled-toggle
  []
  (let [enabled? @(rf/subscribe [::sub/reminders-enabled?])]
    [:div
     {:class "flex justify-between flex-col items-start gap-1"}
     [:p
      {:class "font-semibold"}
      (loc/tr :enable-automated-reminder-emails)]
     [data-input/view
      {:value enabled?
       :editor-type :editor.type/toggle
       :on-change (fn [_value]
                    (rf/dispatch [::event/toggle-reminders-enabled]))}]]))

(defn reminder-config-card-footer
  []
  (let [changes? @(rf/subscribe [::sub/reminder-config-changes?])]
    [:div
     {:class "flex justify-end"}
     [button/sm-primary-solid
      {:disabled? (not changes?)
       :on-click #(rf/dispatch [::event/save-changes])}
      (loc/tr (if changes?
                :save-changes
                :no-changes))]]))

(defn inject-link-blocker-style
  [html]
  (let [style-tag "<style>
                     a {
                       pointer-events: none !important;
                       cursor: default !important;
                       text-decoration: none !important;
                     }
                   </style>"]
    (if (re-find #"</body>" html)
      (string/replace html #"</body>" (str style-tag "</body>"))
      (str html style-tag))))

(defn reminder-email-preview-modal
  []
  (let [templates @(rf/subscribe [::sub/email-templates])
        email-config @(rf/subscribe [::sub/reminder-remail-preview-email-config])
        lang-kw @(rf/subscribe [::sub/reminder-email-lang])
        reminder-email-index @(rf/subscribe [::sub/reminder-email-preview-index])
        template-filename (get-in email-config [:template lang-kw])
        html (-> templates
                 (get template-filename (loc/tr :template-not-found))
                 inject-link-blocker-style)]
    [modal/view
     {:visible? (boolean reminder-email-index)
      :hide! #(rf/dispatch [::event/close-reminder-email-preview])}
     [:div
      (case reminder-email-index
        0 (loc/tr :first-reminder-email-template)
        1 (loc/tr :second-reminder-email-template)
        "")]
     [:div
      {:class "flex flex-col gap-5"}
      [:div
       {:class "flex flex-col items-start gap-1"}
       [very-simple-select
        {:value lang-kw
         :options [{:value :en
                    :label (loc/tr :english)}
                   {:value :fi
                    :label (loc/tr :finnish)}]
         :on-change #(rf/dispatch [::event/set-reminder-email-preview-lang %])}]]
      [:div
       {:class "flex flex-col gap-1"}
       [:p
        {:class "text-base-light-text-paler"}
        (loc/tr :subject)]
       [:div
        {:class "rounded font-semibold text-lg"}
        (get-in email-config [:subject lang-kw])]]

      [:div
       {:class "flex flex-col gap-3"}
       [:p
        {:class "text-base-light-text-paler"}
        (loc/tr :email-content)]

       [:div
        {:class "rounded overflow-hidden"}
        [:iframe
         {:srcDoc html
          :class "rounded"
          :sandbox ""
          :style {:width "100%"
                  :height "800px"}}]]]]

     [:div
      {:class "flex gap-2 py-3"}
      [:i {:class "fal fa-info-circle mt-1"}]
      [:div
       {:class "text-pretty"}
       (loc/tr :email-templates-are-managed-by-our-team-info-text)]]]))

(defn reminders-card-header
  []
  [:div
   {:class "flex flex-col gap-1"}
   [:div
    {:class "flex gap-2 items-center"}
    [:i {:class "fal fa-clock text-lg"}]
    [:h4
     {:class "text-xl font-semibold"}
     (loc/tr :automated-reminders)]]
   [:p
    {:class "text-base-light-text-paler"}
    (loc/tr :automated-kyc-reminders-configuration-info-text)]])

(defn reminder-config-card
  []
  [:div
   {:class "flex flex-col gap-5 rounded border shadow p-4"}
   [reminders-card-header]
   [reminders-enabled-toggle]
   [divider-h]
   [reminders-list]
   [divider-h]
   [reminder-config-card-footer]
   [reminder-email-preview-modal]])

(defn page
  []
  (rf/dispatch-sync [::event/init-editor])
  (r/create-class
   {:reagent-render
    (fn []
      [:<>
       [zap-toolbar/view db/state breadcrumbs nil (constantly [])]
       [:div
        {:class "bg-base-light flex flex-col items-center py-10 px-4"}
        [:div
         {:class "max-w-4xl"}
         [reminder-config-card]]]])}))

(defmethod routing/render-view :settings/kyc
  [_]
  (if (and (session/automated-kyc-renewal?)
           (session/is-admin?))
    [default-page-layout/view
     [page]]
    [not-found/not-found-view]))
